import { WsmErrorCode, WsmErrorFragment } from "@dashboard/graphql";
import commonErrorMessages, { getCommonFormFieldErrorMessage } from "@dashboard/utils/errors/common";
import { defineMessages, IntlShape } from "react-intl";

// The eight compose members of WsmErrorCode. The backend sends its own merchant
// sentence in `message` and the Dashboard renders that verbatim; these strings
// are the fallback for a payload that carries a code and no sentence.
export const wsmComposeErrorMessages = defineMessages({
  configuredFloorBelowZero: {
    defaultMessage: "The cheapest way to configure this product prices at or below zero", id: 'NGW1fl',
  },
  dealerDeltaAboveRetail: {
    defaultMessage: "A dealer pays more than retail for this choice", id: 'dIgHxp',
  },
  dealerFloorBelowZero: {
    defaultMessage: "A dealer group's cheapest configuration prices at or below zero", id: 'a34Y2b',
  },
  duplicateSkuFragment: {
    defaultMessage: "Two choices in this question carry the same SKU fragment", id: 'oz9t9S',
  },
  duplicateTierGroup: {
    defaultMessage: "This choice already has a price for that dealer group", id: 'JJURnD',
  },
  feeAmountNegative: {
    defaultMessage: "A charge cannot be less than nothing", id: 'u/XX4G',
  },
  feePercentAbove100: {
    defaultMessage: "A percentage charge cannot be above 100", id: 'MDUqPI',
  },
  unknownUsStateCode: {
    defaultMessage: "That is not a US state or territory code", id: 'hwGFNN',
  },
});

const codeToMessage = {
  [WsmErrorCode.CONFIGURED_FLOOR_BELOW_ZERO]: wsmComposeErrorMessages.configuredFloorBelowZero,
  [WsmErrorCode.DEALER_DELTA_ABOVE_RETAIL]: wsmComposeErrorMessages.dealerDeltaAboveRetail,
  [WsmErrorCode.DEALER_FLOOR_BELOW_ZERO]: wsmComposeErrorMessages.dealerFloorBelowZero,
  [WsmErrorCode.DUPLICATE_SKU_FRAGMENT]: wsmComposeErrorMessages.duplicateSkuFragment,
  [WsmErrorCode.DUPLICATE_TIER_GROUP]: wsmComposeErrorMessages.duplicateTierGroup,
  [WsmErrorCode.FEE_AMOUNT_NEGATIVE]: wsmComposeErrorMessages.feeAmountNegative,
  [WsmErrorCode.FEE_PERCENT_ABOVE_100]: wsmComposeErrorMessages.feePercentAbove100,
  [WsmErrorCode.UNKNOWN_US_STATE_CODE]: wsmComposeErrorMessages.unknownUsStateCode,
} as Partial<Record<WsmErrorCode, (typeof wsmComposeErrorMessages)["feeAmountNegative"]>>;

/**
 * The model's own sentence first, this module's wording second, the Dashboard's
 * common wording last. Returns undefined for no error, so a helperText prop can
 * take the result directly.
 */
export const getWsmComposeErrorMessage = (
  error: WsmErrorFragment | undefined,
  intl: IntlShape,
): string | undefined => {
  if (!error) {
    return undefined;
  }

  if (error.message) {
    return error.message;
  }

  const message = error.code ? codeToMessage[error.code] : undefined;

  if (message) {
    return intl.formatMessage(message);
  }

  return getCommonFormFieldErrorMessage({ ...error, code: error.code }, intl);
};

export const getWsmComposeFieldError = (errors: WsmErrorFragment[], field: string) =>
  errors.find(error => error.field === field);

/**
 * The backend names a row inside a list input as `<list>.<index>.<field>`, which
 * for the values grid is the only thing that says WHICH choice was refused.
 * Nested tier rows come back as `values.<i>.tierDeltas.<j>.<field>`; those land
 * on the choice they belong to, because that is the row on screen.
 */
export const getWsmIndexedErrors = (
  errors: WsmErrorFragment[],
  list: string,
): Record<number, WsmErrorFragment> =>
  errors.reduce<Record<number, WsmErrorFragment>>((acc, error) => {
    const match = error.field?.match(new RegExp(`^${list}\\.(\\d+)(\\.|$)`));

    if (match && !acc[Number(match[1])]) {
      acc[Number(match[1])] = error;
    }

    return acc;
  }, {});

/** Errors with no `field`: the ones a page shows above its cards. */
export const getWsmComposeFormErrors = (errors: WsmErrorFragment[], intl: IntlShape): string[] =>
  errors
    .filter(error => !error.field)
    .map(
      error =>
        getWsmComposeErrorMessage(error, intl) ??
        intl.formatMessage(commonErrorMessages.unknownError),
    );
